import React, { useState, useEffect, useRef } from 'react';
import CountryFlag, { CountryLabel } from '../../../shared/components/CountryFlag';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Space,
  Alert,
  Table,
  Tag,
  Spin,
  Empty,
  message,
  Divider,
  Input,
  Button,
  Form,
  Select,
  Modal,
  Descriptions,
  Tabs,
  Tooltip,
  Grid
} from 'antd';
import {
  BarChartOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  CloudDownloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import worldJson from '../../../shared/components/world.json';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { useBreakpoint } = Grid;

// 注册世界地图
echarts.registerMap('world', worldJson);

// 国家代码到 ECharts 地图名称的映射
const countryCodeToEchartsName = {
  'CN': 'China', 'US': 'United States', 'JP': 'Japan', 'KR': 'Korea',
  'DE': 'Germany', 'FR': 'France', 'GB': 'United Kingdom', 'RU': 'Russia',
  'BR': 'Brazil', 'IN': 'India', 'AU': 'Australia', 'CA': 'Canada',
  'SG': 'Singapore', 'HK': 'Hong Kong', 'TW': 'Taiwan', 'NL': 'Netherlands',
  'MY': 'Malaysia', 'BG': 'Bulgaria', 'IE': 'Ireland', 'SE': 'Sweden',
  'BE': 'Belgium', 'IT': 'Italy', 'ES': 'Spain', 'PT': 'Portugal',
  'PL': 'Poland', 'UA': 'Ukraine', 'CZ': 'Czech Rep.', 'AT': 'Austria',
  'CH': 'Switzerland', 'NO': 'Norway', 'FI': 'Finland', 'DK': 'Denmark',
  'GR': 'Greece', 'TR': 'Turkey', 'IL': 'Israel', 'SA': 'Saudi Arabia',
  'AE': 'United Arab Emirates', 'TH': 'Thailand', 'VN': 'Vietnam',
  'ID': 'Indonesia', 'PH': 'Philippines', 'NZ': 'New Zealand',
  'MX': 'Mexico', 'AR': 'Argentina', 'CL': 'Chile', 'CO': 'Colombia',
  'ZA': 'South Africa', 'EG': 'Egypt', 'NG': 'Nigeria', 'KE': 'Kenya'
};

// API 返回的名称到 ECharts 名称的映射
const apiNameToEchartsName = {
  'United States of America': 'United States',
  'The Netherlands': 'Netherlands',
  'Korea, Republic of': 'Korea',
  'Russian Federation': 'Russia',
  'Viet Nam': 'Vietnam',
  'Czechia': 'Czech Rep.'
};

export default function Traffic() {
  const { domainData } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);
  const [trafficData, setTrafficData] = useState(null);
  const [topUrls, setTopUrls] = useState([]);
  const [statusCodes, setStatusCodes] = useState([]);
  const [geoData, setGeoData] = useState([]);
  const [panelErrors, setPanelErrors] = useState({});
  const [logs, setLogs] = useState([]);
  const [logDetail, setLogDetail] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchForm] = Form.useForm();

  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (domainData?.id && dateRange && dateRange[0] && dateRange[1]) {
      loadTrafficStats();
    }
  }, [domainData, dateRange]);

  // 初始化和管理地图（带重试，确保容器可见后再初始化）
  useEffect(() => {
    if (activeTab !== 'map' || geoData.length === 0) return;

    let timer = null;
    let resizeHandler = null;
    let cancelled = false;

    const tryInit = (attempt = 0) => {
      if (cancelled) return;
      if (mapRef.current && mapRef.current.offsetWidth > 0 && mapRef.current.offsetHeight > 0) {
        initMap();
        resizeHandler = () => mapInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
      } else if (attempt < 20) {
        timer = setTimeout(() => tryInit(attempt + 1), 50);
      }
    };

    timer = setTimeout(() => tryInit(0), 50);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      if (mapInstance.current && activeTab !== 'map') {
        mapInstance.current.dispose();
        mapInstance.current = null;
      }
    };
  }, [activeTab, geoData]);

  // 切换到请求查询 tab 时自动加载日志
  useEffect(() => {
    if (activeTab === 'logs' && logs.length === 0 && domainData?.id && dateRange?.[0]) {
      searchLogs({});
    }
  }, [activeTab]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.dispose();
        mapInstance.current = null;
      }
    };
  }, []);

  const initMap = () => {
    if (!mapRef.current) return;

    const container = mapRef.current;
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      return;
    }

    if (mapInstance.current) {
      mapInstance.current.dispose();
      mapInstance.current = null;
    }

    mapInstance.current = echarts.init(container);

    const getEchartsName = (item) => {
      if (item.country_code && countryCodeToEchartsName[item.country_code]) {
        return countryCodeToEchartsName[item.country_code];
      }
      if (item.country_name && apiNameToEchartsName[item.country_name]) {
        return apiNameToEchartsName[item.country_name];
      }
      return item.country_name || item.country_code || 'Unknown';
    };

    const mapData = geoData.map(item => ({
      name: getEchartsName(item),
      value: item.requests || 0,
      traffic: item.traffic,
      originalName: item.country_name,
      ...item
    }));

    const scatterData = geoData
      .filter(item => item.latitude && item.longitude)
      .map(item => ({
        name: item.country_name || item.country_code,
        value: [parseFloat(item.longitude), parseFloat(item.latitude), item.requests || 0],
        traffic: item.traffic
      }));

    const maxValue = Math.max(...mapData.map(d => d.value), 1);

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          if (params.seriesType === 'scatter') {
            return `<strong>${params.data.name}</strong><br/>
                    请求数: ${(params.data.value[2] || 0).toLocaleString()}<br/>
                    流量: ${formatBytes(params.data.traffic)}`;
          }
          if (params.value) {
            const displayName = params.data?.originalName || params.name;
            return `<strong>${displayName}</strong><br/>请求数: ${(params.value || 0).toLocaleString()}<br/>流量: ${formatBytes(params.data?.traffic || 0)}`;
          }
          return `<strong>${params.name}</strong><br/>暂无数据`;
        }
      },
      visualMap: {
        min: 0,
        itemWidth: 10,
        itemHeight: 80,
        max: maxValue,
        text: ['高', '低'],
        calculable: false,
        inRange: { color: ['#e0f3f8', '#abd9e9', '#74add1', '#4575b4', '#313695'] },
        left: 'left',
        top: 'bottom'
      },
      geo: {
        map: 'world',
        roam: true,
        zoom: 1.2,
        scaleLimit: { min: 1, max: 10 },
        itemStyle: { areaColor: '#f5f5f5', borderColor: '#d9d9d9', borderWidth: 0.5 },
        emphasis: {
          itemStyle: { areaColor: '#ffd666' },
          label: { show: true }
        }
      },
      series: [
        {
          name: '请求数',
          type: 'map',
          map: 'world',
          geoIndex: 0,
          data: mapData
        },
        ...(scatterData.length > 0 ? [{
          name: '位置',
          type: 'scatter',
          coordinateSystem: 'geo',
          data: scatterData,
          symbolSize: (val) => 4 + 12 * Math.sqrt((val[2] || 0) / maxValue), // 按最大值归一,直径 4~16px
          itemStyle: {
            color: 'rgba(22, 119, 255, 0.75)',
            borderColor: '#fff',
            borderWidth: 1
          }
        }] : [])
      ]
    };

    mapInstance.current.setOption(option);

    setTimeout(() => {
      mapInstance.current?.resize();
    }, 200);
  };

  const loadTrafficStats = async () => {
    setLoading(true);
    const startTime = dateRange[0].unix(); // 发 Unix 秒:服务端按 Asia/Shanghai 解析字符串,非东八区用户会偏移时间窗
    const endTime = dateRange[1].unix();
    const params = { startTime, endTime, domain_id: domainData.id };

    // 各面板独立加载：单个接口失败只影响自己的面板
    const calls = [
      ['overview', '/api/admin/traffic/domain-traffic', (d) => setTrafficData(d)],
      ['topUrls', '/api/admin/traffic/top-urls', (d) => setTopUrls(Array.isArray(d) ? d : [])],
      ['status', '/api/admin/traffic/status-codes', (d) => setStatusCodes(Array.isArray(d) ? d : [])],
      ['map', '/api/admin/traffic/map', (d) => setGeoData(Array.isArray(d) ? d : [])],
    ];
    const settled = await Promise.allSettled(calls.map(([, url]) => apiClient.get(url, { params })));
    const errs = {};
    settled.forEach((r, i) => {
      const [key, , apply] = calls[i];
      const body = r.status === 'fulfilled' ? r.value.data : null;
      if (body?.success) {
        apply(body.data);
      } else {
        errs[key] = body?.error || r.reason?.response?.data?.error || r.reason?.message || '加载失败';
      }
    });
    setPanelErrors(errs);
    const failed = Object.keys(errs);
    if (failed.length) {
      message.error(`${failed.length} 项流量统计加载失败：${errs[failed[0]]}`);
    }
    setLoading(false);
  };

  const searchLogs = async (values) => {
    setLogsLoading(true);
    try {
      const startTime = dateRange[0].unix(); // 发 Unix 秒:服务端按 Asia/Shanghai 解析字符串,非东八区用户会偏移时间窗
      const endTime = dateRange[1].unix();

      const params = {
        startTime,
        endTime,
        domain_id: domainData.id,
        ...values
      };

      const response = await apiClient.get('/api/admin/traffic/logs', { params });
      if (response.data.success) {
        setLogs(response.data.data || []);
        if (response.data.data?.length === 0) {
          message.info('未找到匹配的请求记录');
        }
      } else {
        message.error('查询失败: ' + (response.data.error || '未知错误'));
      }
    } catch (error) {
      message.error('查询失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setLogsLoading(false);
    }
  };

  const viewLogDetail = async (record) => {
    if (record) {
      setLogDetail(record);
      setDetailModalVisible(true);

      try {
        const domainId = record.domain_id || domainData.id;
        const response = await apiClient.get(`/api/admin/traffic/log/${domainId}/${record.request_id}`);
        if (response.data.success) {
          setLogDetail(response.data.data);
        }
      } catch (error) {
        message.warning('详情加载失败，仅显示列表字段');
      }
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num) => new Intl.NumberFormat('zh-CN').format(num || 0);

  const urlColumns = [
    { title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true },
    { title: '访问次数', dataIndex: 'hits', key: 'hits', render: formatNumber, sorter: (a, b) => (a.hits || 0) - (b.hits || 0) },
    { title: '流量', dataIndex: 'bytes', key: 'bytes', render: formatBytes, sorter: (a, b) => (a.bytes || 0) - (b.bytes || 0) }
  ];

  const statusColumns = [
    {
      title: 'HTTP状态码', dataIndex: 'status', key: 'status',
      render: (status) => {
        let color = 'default';
        if (status >= 200 && status < 300) color = 'success';
        else if (status >= 300 && status < 400) color = 'processing';
        else if (status >= 400 && status < 500) color = 'warning';
        else if (status >= 500) color = 'error';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    { title: '次数', dataIndex: 'count', key: 'count', render: formatNumber, sorter: (a, b) => (a.count || 0) - (b.count || 0) }
  ];

  const logColumns = [
    {
      title: '时间', dataIndex: 'timestamp', key: 'timestamp', width: 170,
      render: (t) => t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    { title: '域名', dataIndex: 'host', key: 'host', width: 170, ellipsis: true, render: (h) => h || '-' },
    { title: '方法', dataIndex: 'method', key: 'method', width: 70 },
    {
      title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true,
      // 后端已直接返回完整 url(path + query);兜底再从 uri+args 拼(旧数据)。
      render: (url, r) => {
        const full = url || ((r.uri || '/') + (r.args && r.args !== '-' ? `?${r.args}` : ''));
        return <span title={full}>{full}</span>;
      }
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (status, r) => {
        let color = status >= 200 && status < 300 ? 'success' : status >= 500 ? 'error' : 'warning';
        return (
          <>
            <Tag color={color}>{status}</Tag>
            {r.hops?.length > 1 && <Tag color="purple" title={r.hops.map((h) => `${h.tier.toUpperCase()} ${h.node_id}`).join(' → ')}>{r.hops.length} 跳</Tag>}
          </>
        );
      }
    },
    { title: 'HTTP 版本', dataIndex: 'http_version', key: 'http_version', width: 90, render: (v) => v || '-' },
    { title: '大小', dataIndex: 'bytes', key: 'bytes', width: 90, render: formatBytes },
    { title: '耗时', dataIndex: 'request_time', key: 'request_time', width: 80, render: (t) => `${(t || 0).toFixed(3)}s` },
    { title: '客户端IP', dataIndex: 'client_ip', key: 'client_ip', width: 130 },
    { title: '国家', dataIndex: 'country_code', key: 'country_code', width: 80, render: (c) => (c ? <CountryLabel code={c}>{c}</CountryLabel> : '-') },
    { title: '类型', dataIndex: 'resp_content_type', key: 'resp_content_type', width: 100, ellipsis: true, render: (t) => t ? t.split(';')[0] : '-' },
    { title: '缓存', dataIndex: 'cache_status', key: 'cache_status', width: 70, render: (s) => <Tag color={s === 'HIT' ? 'green' : 'orange'}>{s || '-'}</Tag> },
    {
      title: '操作', key: 'action', width: 60, fixed: 'right',
      render: (_, record) => (
        <Tooltip title="查看详情">
          <Button type="link" icon={<EyeOutlined />} onClick={() => viewLogDetail(record)} />
        </Tooltip>
      )
    }
  ];

  const overview = trafficData?.overview || {};

  const tabItems = [
    {
      key: 'overview',
      label: '概览统计',
      children: (
        <Spin spinning={loading}>
          {panelErrors.overview && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={`概览加载失败：${panelErrors.overview}`} />}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
            <Col xs={12} sm={12} md={6}>
              <Card size={isMobile ? 'small' : 'default'}>
                <Statistic
                  title="总请求数"
                  value={formatNumber(overview.total_requests || 0)}
                  prefix={<GlobalOutlined />}
                  valueStyle={{ color: '#1890ff', fontSize: isMobile ? 18 : 24 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size={isMobile ? 'small' : 'default'}>
                <Statistic
                  title="总流量"
                  value={formatBytes(overview.total_traffic || 0)}
                  prefix={<CloudDownloadOutlined />}
                  valueStyle={{ color: '#3f8600', fontSize: isMobile ? 18 : 24 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size={isMobile ? 'small' : 'default'}>
                <Statistic
                  title="缓存命中率"
                  value={(overview.cache_hit_rate || 0).toFixed(1)}
                  suffix="%"
                  prefix={<DatabaseOutlined />}
                  valueStyle={{ color: (overview.cache_hit_rate || 0) > 80 ? '#3f8600' : '#cf1322', fontSize: isMobile ? 18 : 24 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size={isMobile ? 'small' : 'default'}>
                <Statistic
                  title={isMobile ? '响应时间' : '响应时间中位数'}
                  value={((overview.avg_response_time || 0) * 1000).toFixed(0)}
                  suffix="ms"
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: (overview.avg_response_time || 0) < 0.2 ? '#3f8600' : '#cf1322', fontSize: isMobile ? 18 : 24 }}
                />
              </Card>
            </Col>
          </Row>

          {/* 状态码分布 */}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
            <Col xs={12} sm={12} md={6}>
              <Card size="small">
                <Statistic title={isMobile ? '2xx' : '2xx 成功'} value={formatNumber(overview.status_2xx || 0)} prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a', fontSize: isMobile ? 16 : 18 }} />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small">
                <Statistic title={isMobile ? '3xx' : '3xx 重定向'} value={formatNumber(overview.status_3xx || 0)} prefix={<GlobalOutlined style={{ color: '#1890ff' }} />} valueStyle={{ color: '#1890ff', fontSize: isMobile ? 16 : 18 }} />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small">
                <Statistic title={isMobile ? '4xx' : '4xx 客户端错误'} value={formatNumber(overview.status_4xx || 0)} prefix={<WarningOutlined style={{ color: '#faad14' }} />} valueStyle={{ color: '#faad14', fontSize: isMobile ? 16 : 18 }} />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small">
                <Statistic title={isMobile ? '5xx' : '5xx 服务器错误'} value={formatNumber(overview.status_5xx || 0)} prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />} valueStyle={{ color: '#ff4d4f', fontSize: isMobile ? 16 : 18 }} />
              </Card>
            </Col>
          </Row>

          <Divider style={{ margin: isMobile ? '12px 0' : '24px 0' }} />

          {/* 详细表格 */}
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={24} lg={12}>
              <Card title="热门URL" size="small">
                {panelErrors.topUrls ? <Alert type="error" showIcon message={`加载失败：${panelErrors.topUrls}`} /> : topUrls.length > 0 ? (
                  <Table
                    columns={urlColumns}
                    dataSource={topUrls}
                    rowKey="url"
                    pagination={{ pageSize: isMobile ? 5 : 10, size: 'small' }}
                    size="small"
                    scroll={isMobile ? { x: 400 } : undefined}
                  />
                ) : (
                  <Empty description="暂无数据" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="HTTP状态码分布" size="small">
                {panelErrors.status ? <Alert type="error" showIcon message={`加载失败：${panelErrors.status}`} /> : statusCodes.length > 0 ? (
                  <Table
                    columns={statusColumns}
                    dataSource={statusCodes}
                    rowKey="status"
                    pagination={{ pageSize: isMobile ? 5 : 10, size: 'small' }}
                    size="small"
                  />
                ) : (
                  <Empty description="暂无数据" />
                )}
              </Card>
            </Col>
          </Row>
        </Spin>
      )
    },
    {
      key: 'map',
      label: '地理分布',
      children: (
        <Card title="全球请求分布" size="small">
          {panelErrors.map && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={`加载失败：${panelErrors.map}`} />}
          {geoData.length > 0 ? (
            <div ref={mapRef} style={{ width: '100%', height: isMobile ? 300 : 500 }} />
          ) : (
            <Empty description="暂无地理数据" style={{ padding: isMobile ? 50 : 100 }} />
          )}
          {geoData.length > 0 && (
            <Table
              columns={[
                {
                  title: '国家/地区',
                  dataIndex: 'country_code',
                  key: 'country',
                  ellipsis: true,
                  render: (code, r) => {
                    let name = r.country_name || code || '未知';
                    if (code && countryCodeToEchartsName[code]) name = countryCodeToEchartsName[code];
                    else if (r.country_name && apiNameToEchartsName[r.country_name]) name = apiNameToEchartsName[r.country_name];
                    return <CountryLabel code={code}>{name}</CountryLabel>;
                  }
                },
                { title: '请求数', dataIndex: 'requests', key: 'requests', render: formatNumber, sorter: (a, b) => (a.requests || 0) - (b.requests || 0) },
                { title: '流量', dataIndex: 'traffic', key: 'traffic', render: formatBytes, sorter: (a, b) => (a.traffic || 0) - (b.traffic || 0) }
              ]}
              dataSource={geoData.filter(r => r.country_code)}
              rowKey={(r) => r.country_code}
              pagination={{ pageSize: isMobile ? 5 : 10, size: 'small' }}
              size="small"
              style={{ marginTop: 16 }}
              scroll={isMobile ? { x: 350 } : undefined}
            />
          )}
        </Card>
      )
    },
    {
      key: 'logs',
      label: '请求查询',
      children: (
        <Card title="请求日志查询" size="small">
          <Form
            form={searchForm}
            layout="vertical"
            onFinish={searchLogs}
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[12, 0]}>
              <Col xs={12} sm={8} md={6}>
                <Form.Item name="request_id" label="Request ID">
                  <Input placeholder="请求ID" allowClear />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Form.Item name="uri" label="URI">
                  <Input placeholder="URI路径" allowClear />
                </Form.Item>
              </Col>
              <Col xs={8} sm={4} md={3}>
                <Form.Item name="status" label="状态码">
                  <Select placeholder="选择" allowClear>
                    <Option value={200}>200</Option>
                    <Option value={301}>301</Option>
                    <Option value={302}>302</Option>
                    <Option value={304}>304</Option>
                    <Option value={400}>400</Option>
                    <Option value={403}>403</Option>
                    <Option value={404}>404</Option>
                    <Option value={500}>500</Option>
                    <Option value={502}>502</Option>
                    <Option value={503}>503</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={8} sm={6} md={4}>
                <Form.Item name="client_ip" label="客户端IP">
                  <Input placeholder="IP地址" allowClear />
                </Form.Item>
              </Col>
              <Col xs={8} sm={6} md={6}>
                <Form.Item label=" ">
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={logsLoading}>
                      查询
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => { searchForm.resetFields(); setLogs([]); }}>
                      重置
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Table
            columns={logColumns}
            dataSource={logs}
            rowKey="request_id"
            loading={logsLoading}
            pagination={{
              pageSize: isMobile ? 10 : 20,
              showTotal: isMobile ? undefined : (total) => `共 ${total} 条记录`,
              size: 'small'
            }}
            size="small"
            scroll={{ x: 1460 }}
          />
        </Card>
      )
    }
  ];

  return (
    <div>
      <Card
        title={
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            <span><BarChartOutlined /> 流量分析</span>
            {!isMobile && <span style={{ color: '#666', fontWeight: 'normal' }}>- {domainData?.domain}</span>}
          </Space>
        }
        extra={!isMobile && (
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            placeholder={['开始日期', '结束日期']}
          />
        )}
        size={isMobile ? 'small' : 'default'}
      >
        {/* 移动端显示日期选择器 */}
        {isMobile && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>域名: {domainData?.domain}</div>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder={['开始', '结束']}
              style={{ width: '100%' }}
              size="small"
            />
          </div>
        )}

        <Alert
          message="流量统计"
          description={isMobile ? "查看域名流量使用情况和请求日志" : "查看域名的流量使用情况、地理分布和请求日志。数据来源于 OpenObserve 日志分析。"}
          type="info"
          showIcon
          style={{ marginBottom: isMobile ? 16 : 24 }}
        />

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* 请求详情弹窗 */}
      <Modal
        title="请求详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 900}
        style={isMobile ? { top: 20 } : undefined}
      >
        {logDetail && (
          <Tabs defaultActiveKey="basic" size={isMobile ? 'small' : 'middle'} items={[
            ...(logDetail.hops?.length > 1 ? [{
              key: 'hops',
              label: `回源链路 (${logDetail.hops.length} 跳)`,
              children: (
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(h, i) => i}
                  dataSource={logDetail.hops}
                  scroll={{ x: true }}
                  columns={[
                    { title: '层级', dataIndex: 'tier', render: (t) => <Tag color={t === 'l1' ? 'blue' : t === 'l2' ? 'cyan' : 'purple'}>{String(t).toUpperCase()}</Tag> },
                    { title: '节点', dataIndex: 'node_id' },
                    { title: '下一跳', dataIndex: 'route_type', render: (v) => ({ l2: '经 L2', l3: '经加速节点', direct: '源站', origin: '源站(回退)' }[v] || v || '-') },
                    { title: '状态', dataIndex: 'status' },
                    { title: '缓存', dataIndex: 'cache_status', render: (v) => v || '-' },
                    { title: '字节', dataIndex: 'bytes', render: formatBytes },
                    { title: '耗时', dataIndex: 'request_time', render: (v) => (v != null ? `${Number(v).toFixed(3)}s` : '-') },
                    { title: '回源地址', dataIndex: 'upstream_addr', render: (v) => v || '-' },
                  ]}
                />
              ),
            }] : []),
            {
              key: 'basic',
              label: '基本信息',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="Request ID" span={isMobile ? 1 : 2}><span style={{ wordBreak: 'break-all', fontSize: isMobile ? 11 : 14 }}>{logDetail.request_id}</span></Descriptions.Item>
                  <Descriptions.Item label="时间">{logDetail.timestamp ? dayjs(logDetail.timestamp).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                  <Descriptions.Item label="域名"><span style={{ wordBreak: 'break-all' }}>{logDetail.host}</span></Descriptions.Item>
                  <Descriptions.Item label="方法">{logDetail.method}</Descriptions.Item>
                  <Descriptions.Item label="状态码"><Tag color={logDetail.status >= 200 && logDetail.status < 300 ? 'success' : logDetail.status >= 500 ? 'error' : 'warning'}>{logDetail.status}</Tag></Descriptions.Item>
                  <Descriptions.Item label="URL" span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all' }}>{logDetail.url || ((logDetail.uri || '/') + (logDetail.args && logDetail.args !== '-' ? `?${logDetail.args}` : '')) || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="协议">{logDetail.scheme || '-'}</Descriptions.Item>
                  <Descriptions.Item label="HTTP 版本">{logDetail.http_version || '-'}</Descriptions.Item>
                  <Descriptions.Item label="客户端IP">{logDetail.client_ip}</Descriptions.Item>
                  <Descriptions.Item label="响应大小">{formatBytes(logDetail.bytes)}</Descriptions.Item>
                  <Descriptions.Item label="请求大小">{logDetail.request_length ? formatBytes(logDetail.request_length) : '-'}</Descriptions.Item>
                  <Descriptions.Item label="请求耗时">{(logDetail.request_time || 0).toFixed(3)}s</Descriptions.Item>
                  <Descriptions.Item label="缓存状态"><Tag color={logDetail.cache_status === 'HIT' ? 'green' : 'orange'}>{logDetail.cache_status || '-'}</Tag></Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'headers',
              label: '请求头',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="User-Agent" span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all', fontSize: isMobile ? 11 : 14 }}>{logDetail.user_agent || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="Referer" span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all' }}>{logDetail.referer || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="X-Forwarded-For" span={isMobile ? 1 : 2}>{logDetail.x_forwarded_for || '-'}</Descriptions.Item>
                  <Descriptions.Item label="X-Real-IP">{logDetail.x_real_ip || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Origin">{logDetail.origin_header || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Accept-Language">{logDetail.accept_language || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Accept-Encoding">{logDetail.accept_encoding || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Content-Type">{logDetail.content_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Range">{logDetail.range || '-'}</Descriptions.Item>
                  <Descriptions.Item label="If-Modified-Since">{logDetail.if_modified_since || '-'}</Descriptions.Item>
                  <Descriptions.Item label="If-None-Match">{logDetail.if_none_match || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'response',
              label: '响应头',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="Content-Type" span={isMobile ? 1 : 2}>{logDetail.resp_content_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Cache-Control" span={isMobile ? 1 : 2}>{logDetail.resp_cache_control || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Content-Length">{logDetail.resp_content_length || '-'}</Descriptions.Item>
                  <Descriptions.Item label="ETag">{logDetail.resp_etag || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Last-Modified" span={isMobile ? 1 : 2}>{logDetail.resp_last_modified || '-'}</Descriptions.Item>
                  <Descriptions.Item label="上游Content-Type">{logDetail.up_content_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="上游Cache-Control">{logDetail.up_cache_control || '-'}</Descriptions.Item>
                  <Descriptions.Item label="上游X-Cache">{logDetail.up_x_cache || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'upstream',
              label: '上游',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="上游地址">{logDetail.upstream_addr || '-'}</Descriptions.Item>
                  <Descriptions.Item label="上游状态">{logDetail.upstream_status || '-'}</Descriptions.Item>
                  <Descriptions.Item label="响应时间">{logDetail.upstream_response_time ? `${logDetail.upstream_response_time}s` : '-'}</Descriptions.Item>
                  <Descriptions.Item label="连接时间">{logDetail.upstream_connect_time ? `${logDetail.upstream_connect_time}s` : '-'}</Descriptions.Item>
                  <Descriptions.Item label="头部时间">{logDetail.upstream_header_time ? `${logDetail.upstream_header_time}s` : '-'}</Descriptions.Item>
                  <Descriptions.Item label="L2目标">{logDetail.l2_target || '-'}</Descriptions.Item>
                  <Descriptions.Item label="L2缓存">{logDetail.l2_cache_status || '-'}</Descriptions.Item>
                  <Descriptions.Item label="CDN跳数">{logDetail.cdn_hop || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'ssl',
              label: 'SSL',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="SSL协议">{logDetail.ssl_protocol || '-'}</Descriptions.Item>
                  <Descriptions.Item label="加密套件">{logDetail.ssl_cipher || '-'}</Descriptions.Item>
                  <Descriptions.Item label="JA3指纹" span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all', fontSize: isMobile ? 10 : 12 }}>{logDetail.ja3 || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="JA3 Hash">{logDetail.ja3_hash || '-'}</Descriptions.Item>
                  <Descriptions.Item label="JA4" span={isMobile ? 1 : 2}><div style={{ wordBreak: 'break-all', fontSize: isMobile ? 10 : 12 }}>{logDetail.ja4 || '-'}</div></Descriptions.Item>
                  <Descriptions.Item label="HTTP/2指纹">{logDetail.h2_fp || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'geo',
              label: '地理',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="国家"><CountryFlag code={logDetail.country_code} />{logDetail.country_name || '-'} ({logDetail.country_code || '-'})</Descriptions.Item>
                  <Descriptions.Item label="地区">{logDetail.region || '-'}</Descriptions.Item>
                  <Descriptions.Item label="城市">{logDetail.city || '-'}</Descriptions.Item>
                  <Descriptions.Item label="ISP">{logDetail.isp || '-'}</Descriptions.Item>
                  <Descriptions.Item label="ASN">{logDetail.asn || '-'}</Descriptions.Item>
                  <Descriptions.Item label="AS名称">{logDetail.as_name || '-'}</Descriptions.Item>
                </Descriptions>
              )
            },
            {
              key: 'cdn',
              label: 'CDN',
              children: (
                <Descriptions bordered column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="节点ID">{logDetail.node_id || '-'}</Descriptions.Item>
                  <Descriptions.Item label="节点层级">{logDetail.node_tier || '-'}</Descriptions.Item>
                  <Descriptions.Item label="路由类型">{logDetail.route_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="缓存状态"><Tag color={logDetail.cache_status === 'HIT' ? 'green' : 'orange'}>{logDetail.cache_status || '-'}</Tag></Descriptions.Item>
                </Descriptions>
              )
            }
          ]} />
        )}
      </Modal>
    </div>
  );
}
